import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import deburr from 'lodash/deburr';
import React, { useState, useEffect, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import './SearchableDropdown.scss';

interface SearchableDropdownProps {
    autoCompleteEnabled?: boolean;
    dropdownOptions?: (DropdownOption | null)[];
    hasError?: boolean;
    errorMessage?: string;
    id: string;
    extraProp?: any;
    label: string;
    onSelect: (eventKey: string | null, e?: React.SyntheticEvent<unknown>) => void;
    required?: boolean;
    disabled?: boolean;
    value?: string | number | null | undefined;
    endIcon?: React.ReactElement;
}

export interface DropdownOption {
    id: number | string;
    name: string | number;
}

export const SearchableDropdownV2 = ({
    autoCompleteEnabled = false,
    disabled,
    dropdownOptions,
    errorMessage,
    hasError,
    id,
    extraProp,
    label,
    onSelect,
    required,
    value,
    endIcon,
}: SearchableDropdownProps): React.ReactElement => {
    const [selectedValue, setSelectedValue] = useState<string | number | null | undefined>(value);
    const [state, setState] = React.useState({ single: '' });
    const isTypingRef = useRef(false);
    const highlightedOptionRef = useRef<DropdownOption | null>(null);

    useEffect(() => {
        if (!isTypingRef.current) {
            setSelectedValue(value ?? '');
        }
    }, [value]);

    useEffect(() => {
        if (value !== "" && extraProp) {
                const selectedSuggestion =
                    dropdownOptions && dropdownOptions.length > 0
                        ? dropdownOptions.filter((s: DropdownOption | null) =>
                            s !== null && s.name !== null ? s.name === value : false,
                        )[0]
                        : null;
                if (selectedSuggestion)
                    onSelect(`${selectedSuggestion.id}`);
        }
    }, [extraProp]);


    const handleOnSelect = (eventKey: string | null, e: React.SyntheticEvent<unknown>): void => {
        const target = e.target;
        if (target instanceof HTMLAnchorElement || target instanceof HTMLInputElement) {
            setSelectedValue(target.textContent);
        }
        onSelect(eventKey, e);
    };

    if (autoCompleteEnabled) {
        const getSuggestions = (value: string, opts: DropdownOption[]): DropdownOption[] => {
            const inputValue = deburr(value.trim()).toLowerCase();
            return opts.filter(suggestion =>
                suggestion.name.toString().toLowerCase().includes(inputValue),
            );
        };

        const getSuggestionsCollection = (opts: (DropdownOption | null)[]): DropdownOption[] =>
            opts.length > 0 ? (opts.filter(d => d !== null) as DropdownOption[]) : [];

        const options = getSuggestionsCollection(dropdownOptions || []);
        const inputValueStr = state.single !== '' ? state.single : `${selectedValue ?? ''}`;

        return (
            <div className={`Searchable-Dropdown-Container ${disabled ? 'Searchable-Dropdown-Container-Disabled' : ''}`}>
                <label className={`Searchable-Dropdown-Label ${hasError ? 'Searchable-Dropdown-Error' : ''}`}>
                    {required ? `*${label}` : label}
                </label>
                <div className={`Searchable-Dropdown-InputContainer ${hasError ? 'Searchable-Dropdown-Error-Border' : ''}`}>
                    <Autocomplete
                        freeSolo
                        disableClearable
                        options={options}
                        filterOptions={(opts, { inputValue: iv }) => getSuggestions(iv, opts)}
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : String(opt.name))}
                        value={options.find(o => String(o.name) === inputValueStr) || null}
                        inputValue={inputValueStr}
                        onInputChange={(_e, v) => {
                            setState(s => ({ ...s, single: v }));
                            if (v === '') {
                                isTypingRef.current = false;
                                setSelectedValue(null);
                                onSelect(null, _e as React.SyntheticEvent);
                                return;
                            }
                            const found = options.find(o => String(o.name) === v);
                            if (found) {
                                isTypingRef.current = false;
                                setSelectedValue(String(found.name));
                                onSelect(String(found.id), _e as React.SyntheticEvent);
                            } else {
                                isTypingRef.current = true;
                            }
                        }}
                        onChange={(_e, newValue) => {
                            isTypingRef.current = false;
                            setState(s => ({ ...s, single: '' }));
                            if (newValue && typeof newValue !== 'string') {
                                setSelectedValue(String(newValue.name));
                                onSelect(String(newValue.id), _e as React.SyntheticEvent);
                            } else {
                                setSelectedValue(null);
                                onSelect(null, _e as React.SyntheticEvent);
                            }
                        }}
                        onHighlightChange={(_e, option) => {
                            highlightedOptionRef.current = option;
                        }}
                        renderOption={(props, option) => {
                            const matches = match(String(option.name), inputValueStr);
                            const parts = parse(String(option.name), matches);
                            return (
                                <li {...props} key={option.id}>
                                    <div>
                                        {parts.map((part, i) => (
                                            <span key={i} style={{ fontWeight: part.highlight ? 500 : 400 }}>
                                                {part.text}
                                            </span>
                                        ))}
                                    </div>
                                </li>
                            );
                        }}
                        renderInput={(params) => {
                            const ip = params.inputProps || {};
                            const { size: _size, value: _inputValue, ...inputPropsRest } = ip as typeof ip & { size?: unknown; value?: string | number | readonly string[] };
                            return (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    size="small"
                                    label=""
                                    onBlur={() => {
                                        isTypingRef.current = false;
                                        setState(s => ({ ...s, single: '' }));
                                    }}
                                    inputProps={{
                                        ...inputPropsRest,
                                        value: inputValueStr,
                                        name: id,
                                        disabled: disabled ?? false,
                                        required: hasError ?? false,
                                        autoComplete: 'off',
                                        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                                            if (event.key === 'Tab' && highlightedOptionRef.current) {
                                                isTypingRef.current = false;
                                                const highlighted = highlightedOptionRef.current;
                                                setState(s => ({ ...s, single: '' }));
                                                setSelectedValue(String(highlighted.name));
                                                onSelect(String(highlighted.id), event as unknown as React.SyntheticEvent);
                                            }
                                        },
                                    }}
                                    sx={{
                                        width: '100%',
                                        '& .MuiInputBase-root': { height: 35, fontSize: 16, fontFamily: "'Avenir-Book', sans-serif" },
                                        '& .MuiInputLabel-root': { display: 'none' },
                                    }}
                                />
                            );
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    backgroundColor: 'white',
                                    border: '1px solid #0000008a',
                                    maxHeight: 200,
                                    marginTop: '2px',
                                    zIndex: 1,
                                    overflow: 'hidden',
                                },
                            },
                        }}
                        sx={{ flexGrow: 1, position: 'relative' }}
                        ListboxProps={{
                            style: {
                                margin: 0,
                                padding: 0,
                                listStyleType: 'none',
                                maxHeight: 200,
                                overflow: 'auto',
                                fontFamily: "'Avenir-Book', sans-serif",
                            },
                        }}
                    />
                    {endIcon && <div className="SearchableDropdown-EndIcon">{endIcon}</div>}
                </div>
                {hasError && <label className="Searchable-Dropdown-Error">{errorMessage}</label>}
            </div>
        );
    }

    const dropdownMenuItems =
        dropdownOptions &&
        dropdownOptions.length > 0 &&
        dropdownOptions.map(option => {
            return option && option.id && option.name ? (
                <Dropdown.Item key={option.id} eventKey={`${option.id}`}>
                    {option.name}
                </Dropdown.Item>
            ) : (
                undefined
            );
        });

    return (
        <div className={`Searchable-Dropdown-Container ${disabled ? 'Searchable-Dropdown-Container-Disabled' : ''}`}>
            <label className={`Searchable-Dropdown-Label ${hasError && 'Searchable-Dropdown-Error'}`}>
                {required ? `*${label}` : label}
            </label>
            <Dropdown onSelect={handleOnSelect} className={`${hasError && 'Searchable-Dropdown-Error-Border'}`}>
                <DropdownButton disabled={disabled} id={id} title={selectedValue ? selectedValue : ''}>
                    {dropdownMenuItems}
                </DropdownButton>
            </Dropdown>
            {hasError && <label className="Searchable-Dropdown-Error">{errorMessage}</label>}
        </div>
    );
};

export default SearchableDropdownV2;
